import {useEffect, useRef} from 'react';

interface ContextMenuScrollGuardParams {
  isOpen: boolean;
  /** Whether the event target lies inside the menu (including any flyout/submenu surfaces), so the menu's own overflow can still scroll. */
  isEventTargetInsideMenu: (target: EventTarget | null) => boolean;
  /** Called when the content scrolls anyway (programmatic scrolls the input block can't stop) — close the menu instead of letting it detach. */
  onScrollEscape: () => void;
}

/**
 * Native-context-menu scroll semantics for a menu anchored to a point in scrollable content:
 * while the menu is open, wheel/touch scrolling outside it is swallowed so the content cannot
 * move under the menu, and any scroll that happens regardless (e.g. the timeline's playhead
 * recentering or scroll-into-view) closes the menu via onScrollEscape. Clicks and scrollbar
 * drags need no handling here — the menu's own outside-pointerdown close fires first.
 */
export function useContextMenuScrollGuard({isOpen, isEventTargetInsideMenu, onScrollEscape}: ContextMenuScrollGuardParams): void {
  // Kept in refs so the document listeners subscribe once per open, not on every render of the caller.
  const isEventTargetInsideMenuRef = useRef(isEventTargetInsideMenu);
  const onScrollEscapeRef = useRef(onScrollEscape);
  isEventTargetInsideMenuRef.current = isEventTargetInsideMenu;
  onScrollEscapeRef.current = onScrollEscape;

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    const blockScrollInputOutsideMenu = (event: Event): void => {
      if (!isEventTargetInsideMenuRef.current(event.target)) {
        event.preventDefault();
        event.stopPropagation();
      }
    };
    const closeOnOutsideScroll = (event: Event): void => {
      if (!isEventTargetInsideMenuRef.current(event.target)) {
        onScrollEscapeRef.current();
      }
    };
    // Capture phase so the block runs before any scroller or zoom handler; passive:false so preventDefault works.
    document.addEventListener('wheel', blockScrollInputOutsideMenu, {capture: true, passive: false});
    document.addEventListener('touchmove', blockScrollInputOutsideMenu, {capture: true, passive: false});
    document.addEventListener('scroll', closeOnOutsideScroll, {capture: true, passive: true});
    return (): void => {
      document.removeEventListener('wheel', blockScrollInputOutsideMenu, {capture: true});
      document.removeEventListener('touchmove', blockScrollInputOutsideMenu, {capture: true});
      document.removeEventListener('scroll', closeOnOutsideScroll, {capture: true});
    };
  }, [isOpen]);
}
